import { useState, type ReactNode } from 'react'
import { Check, ChevronDown, Filter, X } from 'lucide-react'
import { Button, Dropdown, SearchInput } from '@/components/ui'
import { cn } from '@/internationalization/cn'
import { PLATFORM_LABEL } from '@/internationalization/constants'
import { useT } from '@/internationalization/i18n'
import { PatronFilter } from '@/features/patrons'
import type { MeetingListParams } from '../api'
import {
  STATUSES,
  currentLeaveReasonOf,
  isStatusOptionSelected,
  statusOptionPatch,
  toggleOption,
} from './meetingStatusOptions'

// Platform names are proper nouns and are not translated.
const PLATFORMS = [
  { value: 'all', key: 'meetings.platform.all', label: null as string | null },
  { value: 'teams', key: null, label: PLATFORM_LABEL.teams },
  { value: 'meet', key: null, label: PLATFORM_LABEL.meet },
  { value: 'zoom', key: null, label: PLATFORM_LABEL.zoom },
  { value: 'device', key: null, label: PLATFORM_LABEL.device },
]

const FIELD_CLASS =
  'w-full rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-fg outline-none placeholder:text-fg-subtle focus:border-brand'

function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid grid-cols-[7rem_1fr] items-center gap-3 border-b border-border px-4 py-3">
      <span className="text-sm font-semibold text-fg">{label}</span>
      {children}
    </label>
  )
}

/** A collapsed-by-default filter group (one open at a time); the header shows the current choice. */
function FilterSection({
  title,
  summary,
  isDefault,
  onClear,
  open,
  onToggle,
  options,
}: {
  title: string
  summary: string
  isDefault: boolean
  onClear: () => void
  open: boolean
  onToggle: () => void
  options: { id: string; label: string; selected: boolean; onSelect: () => void }[]
}) {
  return (
    <div className="border-b border-border">
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <ChevronDown
            aria-hidden
            className={cn('h-4 w-4 shrink-0 text-fg-subtle transition-transform', !open && '-rotate-90')}
          />
          <span className="text-sm font-semibold text-fg">{title}</span>
          {isDefault && <span className="ml-auto truncate text-xs text-fg-subtle">{summary}</span>}
        </button>
        {!isDefault && (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand-subtle px-2 py-0.5 text-xs text-brand">
            {summary}
            <button type="button" aria-label={`${title}: ${summary}`} onClick={onClear}>
              <X aria-hidden className="h-3 w-3" />
            </button>
          </span>
        )}
      </div>
      {open && (
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 px-4 pb-3 sm:grid-cols-3">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              role="menuitemradio"
              aria-checked={o.selected}
              onClick={o.onSelect}
              className="flex items-center gap-2 rounded-md px-1 py-1.5 text-left text-sm text-fg hover:bg-surface-muted"
            >
              <span
                aria-hidden
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                  o.selected ? 'border-brand bg-brand text-brand-fg' : 'border-border bg-surface',
                )}
              >
                {o.selected && <Check className="h-3 w-3" />}
              </span>
              <span className="truncate">{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

type FilterDraft = {
  statusId: string
  platform: string
  organizer: string
  participant: string
  dateFrom: string
  dateTo: string
}

const EMPTY_DRAFT: FilterDraft = {
  statusId: 'all',
  platform: 'all',
  organizer: '',
  participant: '',
  dateFrom: '',
  dateTo: '',
}

/**
 * The Filters menu body. It mounts each time the menu opens, so the draft
 * starts from the URL params and Cancel (or clicking away) discards it; only
 * Apply writes to the params.
 */
function FiltersPanel({
  params,
  onChange,
  close,
}: {
  params: MeetingListParams
  onChange: (patch: Partial<MeetingListParams>) => void
  close: () => void
}) {
  const t = useT()
  const [draft, setDraft] = useState<FilterDraft>({
    statusId: (STATUSES.find((s) => isStatusOptionSelected(s, params)) ?? STATUSES[0]).id,
    platform: params.platform ?? 'all',
    organizer: params.organizer ?? '',
    participant: params.participant ?? '',
    dateFrom: params.dateFrom ?? '',
    dateTo: params.dateTo ?? '',
  })
  const [openSection, setOpenSection] = useState<'status' | 'platform' | null>(null)
  const set = (patch: Partial<FilterDraft>) => setDraft((d) => ({ ...d, ...patch }))
  const toggleSection = (id: 'status' | 'platform') => setOpenSection((v) => (v === id ? null : id))
  const platformLabel = (p: (typeof PLATFORMS)[number]) => (p.key ? t(p.key) : (p.label as string))
  const status = STATUSES.find((s) => s.id === draft.statusId) ?? STATUSES[0]
  const platform = PLATFORMS.find((p) => p.value === draft.platform) ?? PLATFORMS[0]
  const draftCount =
    Number(draft.statusId !== 'all') +
    Number(draft.platform !== 'all') +
    Number(Boolean(draft.organizer)) +
    Number(Boolean(draft.participant)) +
    Number(Boolean(draft.dateFrom || draft.dateTo))

  const apply = () => {
    onChange({
      ...statusOptionPatch(status),
      platform: draft.platform,
      organizer: draft.organizer,
      participant: draft.participant,
      dateFrom: draft.dateFrom,
      dateTo: draft.dateTo,
      page: 1,
    })
    close()
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <FilterSection
          title={t('meetings.filter.status')}
          summary={t(status.key)}
          isDefault={draft.statusId === 'all'}
          onClear={() => set({ statusId: 'all' })}
          open={openSection === 'status'}
          onToggle={() => toggleSection('status')}
          options={STATUSES.map((s) => ({
            id: `status-${s.id}`,
            label: t(s.key),
            selected: s.id === draft.statusId,
            onSelect: () => {
              set({ statusId: toggleOption(draft.statusId, s.id) })
              setOpenSection(null)
            },
          }))}
        />
        <FilterSection
          title={t('meetings.filter.platform')}
          summary={platformLabel(platform)}
          isDefault={draft.platform === 'all'}
          onClear={() => set({ platform: 'all' })}
          open={openSection === 'platform'}
          onToggle={() => toggleSection('platform')}
          options={PLATFORMS.map((p) => ({
            id: `platform-${p.value}`,
            label: platformLabel(p),
            selected: p.value === draft.platform,
            onSelect: () => {
              set({ platform: toggleOption(draft.platform, p.value) })
              setOpenSection(null)
            },
          }))}
        />
        <FilterRow label={t('meetings.filter.organizer')}>
          <input
            type="text"
            value={draft.organizer}
            onChange={(e) => set({ organizer: e.target.value })}
            maxLength={254}
            placeholder={t('meetings.filter.namePlaceholder')}
            className={FIELD_CLASS}
          />
        </FilterRow>
        <FilterRow label={t('meetings.filter.participants')}>
          <input
            type="text"
            value={draft.participant}
            onChange={(e) => set({ participant: e.target.value })}
            maxLength={254}
            placeholder={t('meetings.filter.namePlaceholder')}
            className={FIELD_CLASS}
          />
        </FilterRow>
        <div className="grid grid-cols-1 gap-2 px-4 py-3 sm:grid-cols-[7rem_1fr] sm:items-center sm:gap-3">
          <span className="text-sm font-semibold text-fg">{t('meetings.filter.dateRange')}</span>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={draft.dateFrom}
              max={draft.dateTo || undefined}
              onChange={(e) => set({ dateFrom: e.target.value })}
              aria-label={t('meetings.filter.dateFrom')}
              className={cn(FIELD_CLASS, 'min-w-0')}
            />
            <span aria-hidden className="text-fg-subtle">–</span>
            <input
              type="date"
              value={draft.dateTo}
              min={draft.dateFrom || undefined}
              onChange={(e) => set({ dateTo: e.target.value })}
              aria-label={t('meetings.filter.dateTo')}
              className={cn(FIELD_CLASS, 'min-w-0')}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 border-t border-border bg-surface-raised px-4 py-3">
        <Button variant="ghost" size="sm" onClick={() => setDraft(EMPTY_DRAFT)}>
          {t('meetings.filter.clearAll')}
        </Button>
        <Button variant="secondary" size="sm" className="ml-auto" onClick={close}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" size="sm" onClick={apply}>
          {t('meetings.filter.apply')}
          {draftCount > 0 && ` (${draftCount})`}
        </Button>
      </div>
    </div>
  )
}

/**
 * Search, a Patron filter, and a Filters menu (status, platform, organiser,
 * participants, date range). On mobile the search field takes the full width and
 * the filters sit under it — a filter bar that scrolls sideways hides the
 * filters that are switched on.
 *
 * Filter state lives in the URL (see MeetingsPage), so a filtered list is a
 * shareable link and the back button undoes a filter.
 */
export function MeetingFilters({
  params,
  onChange,
  onReset,
}: {
  params: MeetingListParams
  onChange: (patch: Partial<MeetingListParams>) => void
  onReset: () => void
}) {
  const t = useT()
  const currentLeaveReason = currentLeaveReasonOf(params)
  // Status and platform live inside the Filters menu; the button shows how many
  // are on, since the menu hides which ones.
  const activeCount =
    Number((params.status ?? 'all') !== 'all' || currentLeaveReason !== undefined) +
    Number((params.platform ?? 'all') !== 'all') +
    Number(Boolean(params.organizer)) +
    Number(Boolean(params.participant)) +
    Number(Boolean(params.dateFrom || params.dateTo))
  const hasFilters =
    activeCount > 0 ||
    Boolean(params.q) ||
    (params.status ?? 'all') !== 'all' ||
    currentLeaveReason !== undefined ||
    (params.platform ?? 'all') !== 'all' ||
    (params.patron ?? 'all') !== 'all'

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <SearchInput
        value={params.q ?? ''}
        onChange={(q) => onChange({ q, page: 1 })}
        placeholder={t('meetings.searchPlaceholder')}
        className="sm:max-w-sm"
        aria-label={t('meetings.searchAria')}
      />
      <div className="flex items-center gap-2">
        <PatronFilter
          value={params.patron ?? 'all'}
          onChange={(patron) => onChange({ patron, page: 1 })}
        />
        <Dropdown
          align="start"
          trigger={({ toggle }) => (
            <Button size="md" leftIcon={<Filter className="h-4 w-4" />} onClick={toggle}>
              {t('meetings.filters')}
              {activeCount > 0 && ` (${activeCount})`}
            </Button>
          )}
          items={[]}
          menuClassName="flex w-[min(34rem,calc(100vw-2rem))] max-h-[min(80vh,38rem)] flex-col py-0"
        >
          {({ close }) => <FiltersPanel params={params} onChange={onChange} close={close} />}
        </Dropdown>
        {hasFilters && (
          <Button variant="ghost" size="md" onClick={onReset}>
            {t('common.clear')}
          </Button>
        )}
      </div>
    </div>
  )
}
